import { startOfDay } from '../../date/calendar'
import { parseRecurrencePhrase } from '../../recurrence/phrase'
import { startsWord, wordsOf } from '../boundaries'
import type { Candidate, Recognizer } from './types'

/** So viele Wörter darf ein Wiederholungs-Satz höchstens haben („jeden 2. Monat am 15. bis 31.12.“). */
const MAX_WORDS = 8

/**
 * Hier endet der Satz: Eine Uhrzeit gehört dem Wann, nicht der Regel („jeden Montag 9 Uhr“). Ohne
 * diese Grenze schluckte die Regel-Erkennung die Uhrzeit — sie wirft sie beim Lesen weg.
 *
 * Eine Zahl allein ist nur eine Uhrzeit, wenn „Uhr“, „am“ oder „pm“ folgt — „alle 2 Wochen“ nicht.
 */
const TIME_WORD = /^(\d{1,2}[:.]\d{2}|\d{1,2}(uhr|h|am|pm)|um|at|uhr)$/iu
const TIME_UNIT = /^(uhr|h|am|pm|a\.m\.|p\.m\.)$/iu

const strip = (word: string) => word.replace(/[,;:]+$/u, '')

function isTimeAt(words: { text: string }[], index: number): boolean {
  const word = strip(words[index]!.text)
  if (TIME_WORD.test(word)) return true
  const next = words[index + 1]
  return /^\d{1,2}$/u.test(word) && next !== undefined && TIME_UNIT.test(strip(next.text))
}

/** „3x“, „3“, „3-mal“ — damit beginnt eine Quote („3x pro Woche“). */
const COUNT_WORD = /^\d+(x|×|-?mal)?$/iu

/**
 * **Wiederholung** — ab einem Leitwort die längste Wortfolge, die `parseRecurrencePhrase` annimmt.
 * Der Anker ist heute: „jede Woche“ heißt dann „jeden Freitag“, wenn heute Freitag ist.
 */
export const recognizeRecurrence: Recognizer = ({ text, masked, options, lang }) => {
  const words = wordsOf(masked)
  const today = startOfDay(options.now)
  for (let i = 0; i < words.length; i++) {
    const lead = words[i]!.text.toLowerCase().replace(/!$/u, '')
    if (!(lang.recurrenceLeads.includes(lead) || COUNT_WORD.test(lead)) || !startsWord(text, words[i]!.start)) continue

    let last = i
    while (last + 1 < words.length && last + 1 - i < MAX_WORDS && !isTimeAt(words, last + 1)) last++

    for (let j = last; j >= i; j--) {
      const start = words[i]!.start
      const end = words[j]!.start + strip(words[j]!.text).length
      const rule = parseRecurrencePhrase(masked.slice(start, end), today)
      if (rule) return [{ kind: 'recurrence', start, end, text: text.slice(start, end), value: rule } satisfies Candidate]
    }
  }
  return []
}
